import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adHocFeedbackKeys } from "@/lib/query";
import type { GiveAdHocFeedbackInput } from "@/schemas/review-adhoc-feedback";

/** Standalone feedback given outside any review cycle — EPIC-067. */

export interface AdHocFeedbackItem {
  id: string;
  body: string;
  sentimentKey: string;
  sentimentName: string;
  isAnonymous: boolean;
  isVisibleToEmployee: boolean;
  authorName: string | null;
  /** Set only for post-project feedback — EPIC-068 WI-250. */
  relatedProjectName: string | null;
  createdAt: string;
  /** Shared with you and not looked at yet — only ever set on your own list. */
  isNew: boolean;
}

export interface AdHocFeedbackForPerson {
  role: "self" | "manager" | "admin" | "none";
  items: AdHocFeedbackItem[];
}

export interface FeedbackProject {
  projectId: string;
  projectName: string;
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => null);
  const message =
    body && typeof body === "object" && "error" in body && typeof body.error === "string"
      ? body.error
      : fallback;
  return new Error(message);
}

/** A person's ad hoc feedback, scoped by the server to what the viewer may see. */
export function useAdHocFeedbackForPerson(personId: string, enabled = true) {
  return useQuery({
    queryKey: adHocFeedbackKeys.forPerson(personId),
    enabled: enabled && Boolean(personId),
    queryFn: async (): Promise<AdHocFeedbackForPerson> => {
      const res = await fetch(`/api/feedback/ad-hoc/person/${personId}`);
      if (!res.ok) throw await failure(res, "Couldn't load feedback");
      return (await res.json()).data;
    },
  });
}

export function useGiveAdHocFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: GiveAdHocFeedbackInput) => {
      const res = await fetch("/api/feedback/ad-hoc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't submit that feedback");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: adHocFeedbackKeys.forPerson(variables.subjectPersonId),
      });
      toast.success("Feedback submitted");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Every active NOS project, for the post-project feedback list box — EPIC-068
 * WI-250. Fetched when the list is first opened, then kept: the server holds
 * it for five minutes anyway, so refetching on every open would buy nothing.
 */
export function useFeedbackProjects(enabled: boolean) {
  return useQuery({
    queryKey: adHocFeedbackKeys.projects,
    queryFn: async (): Promise<FeedbackProject[]> => {
      const res = await fetch("/api/feedback/projects");
      if (!res.ok) throw await failure(res, "Couldn't reach NOS to list projects");
      return (await res.json()).data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * How much feedback has been shared with you that you haven't looked at yet —
 * the badge on your Reviews tab and the card on My Dashboard. An error (the
 * feature being off answers 404) reads as "nothing new", so it isn't retried.
 */
export function useMyUnseenFeedbackCount(enabled = true) {
  return useQuery({
    queryKey: adHocFeedbackKeys.unseen,
    enabled,
    queryFn: async (): Promise<number> => {
      const res = await fetch("/api/me/feedback/unseen");
      if (!res.ok) throw await failure(res, "Couldn't check for new feedback");
      return (await res.json()).data.count;
    },
    retry: false,
  });
}

/**
 * Marks the notes your screen just showed you as seen. It happens because you
 * looked, not because you clicked, so it reports a failure but stays quiet on
 * success — a toast every time you open a tab would be noise.
 */
export function useMarkFeedbackSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (feedbackIds: string[]) => {
      const res = await fetch("/api/me/feedback/seen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedbackIds }),
      });
      if (!res.ok) throw await failure(res, "Couldn't mark your feedback as read");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adHocFeedbackKeys.unseen }),
    onError: (error: Error) => toast.error(error.message),
  });
}

/** The manager's release decision for one ad hoc note. */
export function useSetAdHocFeedbackVisibility(subjectPersonId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      feedbackId,
      isVisibleToEmployee,
    }: {
      feedbackId: string;
      isVisibleToEmployee: boolean;
    }) => {
      const res = await fetch(`/api/feedback/ad-hoc/${feedbackId}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isVisibleToEmployee }),
      });
      if (!res.ok) throw await failure(res, "Couldn't change who sees this");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adHocFeedbackKeys.forPerson(subjectPersonId) });
      toast.success(
        variables.isVisibleToEmployee ? "Shared with them" : "No longer shared with them",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** A finished project the viewer is invited to give feedback on — EPIC-068 WI-249. */
export interface ProjectFeedbackPrompt {
  id: string;
  title: string;
  endedOn: string | null;
  nosProjectId: string | null;
  projectName: string | null;
  colleagues: { personId: string; name: string }[];
}

/**
 * The viewer's end-of-project prompts. An error (the feature being off answers
 * 404) is treated as "nothing to show" by the card, so it is not retried.
 */
export function useMyProjectPrompts() {
  return useQuery({
    queryKey: adHocFeedbackKeys.projectPrompts,
    queryFn: async (): Promise<ProjectFeedbackPrompt[]> => {
      const res = await fetch("/api/me/project-feedback-prompts");
      if (!res.ok) throw await failure(res, "Failed to load your project feedback prompts");
      return (await res.json()).data;
    },
    retry: false,
  });
}

export function useDismissProjectPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/me/project-feedback-prompts/${id}/dismiss`, { method: "POST" });
      if (!res.ok) throw await failure(res, "Couldn't dismiss that");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adHocFeedbackKeys.projectPrompts }),
    onError: (error: Error) => toast.error(error.message),
  });
}
